import { avatarClasses } from '@mui/material/Avatar';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { toggleButtonClasses } from '@mui/material/ToggleButton';
import { alpha, CSSObject, Theme } from '@mui/material/styles';
import { brand, gray } from '../themePrimitives';

export const AnotherInput = {
    MuiToggleButton: {
        styleOverrides: {
            root: ({ theme }: { theme: Theme }): CSSObject => ({
                'padding': '12px 16px',
                'textTransform': 'none',
                'borderRadius': '10px',
                'fontWeight': 500,
                'color': gray[400],
                [`&.${toggleButtonClasses.selected}`]: {
                    color: brand[300]
                },
                '&:hover': {
                    backgroundColor: alpha(brand[300], 0.1)
                },
                ...(theme.palette.mode === 'dark' && {
                    backgroundColor: alpha(gray[900], 0.2)
                })
            })
        }
    },
    MuiCheckbox: {
        defaultProps: {
            disableRipple: true,
            icon: (
                <CheckBoxOutlineBlankRoundedIcon sx={{ color: 'hsla(210, 0%, 0%, 0.0)' }} />
            ),
            checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
            indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />
        },
        styleOverrides: {
            root: ({ theme }: { theme: Theme }): CSSObject => ({
                'margin': 10,
                'height': 16,
                'width': 16,
                'borderRadius': 5,
                'border': '1px solid',
                'borderColor': alpha(gray[300], 0.8),
                'boxShadow': '0 0 0 1.5px hsla(210, 0%, 0%, 0.04) inset',
                'backgroundColor': alpha(gray[100], 0.4),
                'transition': 'border-color, background-color, 120ms ease-in',
                '&:hover': {
                    borderColor: brand[300]
                },
                '&.Mui-focusVisible': {
                    outline: `3px solid ${alpha(brand[300], 0.5)}`,
                    outlineOffset: '2px',
                    borderColor: brand[300]
                },
                '&.Mui-checked': {
                    'color': 'white',
                    'backgroundColor': brand[300],
                    'borderColor': brand[300],
                    'boxShadow': 'none',
                    '&:hover': {
                        backgroundColor: brand[600]
                    }
                },
                ...(theme.palette.mode === 'dark' && {
                    'borderColor': 'white',
                    'backgroundColor': alpha(gray[900], 0.8),
                    '&:hover': {
                        borderColor: brand[300]
                    },
                    '&.Mui-focusVisible': {
                        borderColor: brand[400],
                        outline: `3px solid ${alpha(brand[500], 0.5)}`,
                        outlineOffset: '2px'
                    }
                })
            })
        }
    },
    MuiInputBase: {
        styleOverrides: {
            root: {
                border: 'none'
            },
            input: {
                '&::placeholder': {
                    opacity: 0.7,
                    color: gray[500]
                }
            }
        }
    },
    MuiOutlinedInput: {
        styleOverrides: {
            input: ({ theme }: { theme: Theme }): CSSObject => ({
                'padding': 0,
                'fontSize': '14px',
                'fontWeight': '400',
                '&::placeholder': {
                    color: theme.palette.mode === 'dark' ? 'white' : gray[500],
                    opacity: 1
                }
            }),
            root: ({ theme }: { theme: Theme }): CSSObject => ({
                'padding': '8px 12px',
                'color': 'rgba(51,56,63,1)',
                'borderRadius': theme.shape.borderRadius,
                'border': `1px solid ${theme.palette.divider}`,
                'backgroundColor': theme.palette.background.default,
                'transition': 'border 120ms ease-in',
                '&:hover': {
                    borderColor: gray[400]
                },
                [`&.${outlinedInputClasses.focused}`]: {
                    outline: `3px solid ${alpha(brand[500], 0.5)}`,
                    borderColor: brand[400]
                },
                ...(theme.palette.mode === 'dark' && {
                    borderColor: 'white',
                    color: 'white'
                }),
                'variants': [
                    {
                        props: {
                            size: 'small'
                        },
                        style: {
                            minHeight: '2.25rem'
                        }
                    },
                    {
                        props: {
                            size: 'medium'
                        },
                        style: {
                            minHeight: '2.5rem'
                        }
                    }
                ]
            }),
            notchedOutline: {
                border: 'none'
            }
        }
    },
    MuiInputAdornment: {
        styleOverrides: {
            root: ({ theme }: { theme: Theme }): CSSObject => ({
                color: theme.palette.grey[500],
                ...(theme.palette.mode === 'dark' && {
                    color: theme.palette.grey[400]
                })
            })
        }
    },
    MuiFormLabel: {
        styleOverrides: {
            root: ({ theme }: { theme: Theme }): CSSObject => ({
                typography: theme.typography.caption,
                marginBottom: 8
            })
        }
    },
    MuiTypography: {
        styleOverrides: {
            root: () => ({
                '&.MuiTypography-h6': {
                    whiteSpace: 'nowrap',
                    width: '100%',
                    maxWidth: '300px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }
            })
        }
    },
    MuiAvatar: {
        styleOverrides: {
            root: ({ theme }: { theme: Theme }): CSSObject => ({
                'width': 40,
                'height': 40,
                'fontSize': '1rem',
                'backgroundColor': 'white',
                'color': theme.palette.common.white,
                'objectFit': 'contain',
                '&:hover': {
                    backgroundColor: 'none'
                },
                [`.${avatarClasses.img}`]: {
                    objectFit: 'contain'
                },
                ...(theme.palette.mode === 'dark' && {
                    backgroundColor: 'transparent'
                })
            })
        }
    }
    // MuiTabs: {
    //     styleOverrides: {
    //         root: {
    //             borderBottom: `1px solid ${alpha(brand[300], 0.5)}`,
    //             backgroundColor: brand[50]
    //         },
    //         indicator: {
    //             backgroundColor: brand[500],
    //             height: '4px'
    //         }
    //     }
    // },
    // MuiTab: {
    //     styleOverrides: {
    //         root: {
    //             'textTransform': 'none',
    //             'minWidth': 100,
    //             'fontWeight': 500,
    //             'padding': '12px 16px',
    //             '&.Mui-selected': {
    //                 color: brand[500]
    //             },
    //             '&:hover': {
    //                 color: brand[600],
    //                 opacity: 1
    //             }
    //         }
    //     }
    // }
};